// src/hooks/sync/useJournalStreakSync.js
// Publishes the user's journal streak to journalStreaks/{uid}, readable only by their mentor.
// Called from useSharedPrayerSync (mounted once in Layout), which passes in the partner list.
import { useEffect, useRef } from 'react';
import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { STORE_DAILY_ENTRIES, STORE_USER_PROFILE } from '@/lib/storeKeys';
import { resolveAudience } from '@/lib/prayerAudience';
import { computeJournalStreak } from '@/lib/journalStreak';
import { formatDate } from '@/utils/helpers';

const DEBOUNCE_MS = 3000;

export function useJournalStreakSync({ linked, loaded }) {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;

  const [dailyEntries, , entriesReady] = useSyncStore(STORE_DAILY_ENTRIES, {});
  const [profile, , profileReady] = useSyncStore(STORE_USER_PROFILE, {});
  const lastSigRef = useRef(null);

  useEffect(() => {
    if (!uid || !db || !entriesReady || !profileReady || !loaded) return undefined;

    const timer = setTimeout(async () => {
      const todayKey = formatDate(new Date());
      const { streak, lastDate } = computeJournalStreak(dailyEntries, todayKey);
      const audience = resolveAudience('mentor', {
        linkedUids: new Set((linked || []).map((p) => p.uid)),
        mentorUid:  profile?.mentorUid || '',
      });
      const sig = JSON.stringify([streak, lastDate, audience]);
      if (sig === lastSigRef.current) return;
      try {
        if (audience.length === 0) {
          await deleteDoc(doc(db, 'journalStreaks', uid));
        } else {
          await setDoc(doc(db, 'journalStreaks', uid), {
            ownerUid: uid,
            streak,
            lastDate,
            audience,
            updatedAt: serverTimestamp(),
          });
        }
        lastSigRef.current = sig;
      } catch (err) {
        console.warn('[useJournalStreakSync] publish failed:', err?.code || err);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [uid, dailyEntries, entriesReady, profile, profileReady, linked, loaded]);
}

export default useJournalStreakSync;